/**
 * Pinia Store: Newsletter Newsletter
 *
 * Migrated from Vuex: src/extensions/newsletter/store/modules/newsletter.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import NewsletterApi from '@/extensions/newsletter/store/api/newsletter';
import SubscriptionTopicApi from '@/extensions/newsletter/store/api/subscriptionTopic';
import CampaignApi from '@/extensions/marketing/store/api/campaign';
import CustomerGroupApi from '@/extensions/newsletter/store/api/customerGroup';
import MemberTagApi from '@/extensions/newsletter/store/api/memberTag';
import RestrictedAreaMemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import SentEmailApi from '@/extensions/newsletter/store/api/sentEmail';

import RecipientCountQuery from '@/extensions/newsletter/store/graphql/queries/RecipientCount.gql';
import { adminClient as apolloClient } from '@/store/apollo';
import { useSetupStore } from '@/stores';

const form = {
    id: undefined,
    subject: '',
    preheader: '',
    subscriptionTopics: undefined,
    customerGroups: undefined,
    memberTags: undefined,
    campaign: undefined,
    calculatedRecipientCount: null,
    calculatedRecipientCountLoading: false,
    is_pos_template: false,
    pos_restriction: 'all',
    pointsOfSale: [],
};

const rules = {
    subject: 'required',
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false,
    }
};

export const useNewsletterNewsletterStore = defineStore('newsletterNewsletter', {
    state: () => ({
        loading: true,
        newslettersPaginated: {
            path: null,
            variables: {
                order: 'created_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            subscriptionTopic: {
                loading: false,
                items: []
            },
            customerGroup: {
                loading: false,
                items: []
            },
            memberTag: {
                loading: false,
                items: []
            },
            campaign: {
                loading: false,
                items: []
            },
        },
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui),
        // Sent emails paginated list
        sentEmailsPaginated: {
            loading: false,
            variables: {
                order: 'created_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            items: []
        },
        // URL clicks modal data
        urlClicks: {
            loading: false,
            visible: false,
            items: []
        },
        // URL clickers list data (users who clicked a specific URL)
        urlClickers: {
            loading: false,
            items: [],
            url: null,
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            }
        },
        // Bulk tagging state
        bulkTagging: {
            loading: false
        },
        // Statistics timeline chart data
        statisticsTimeline: {
            loading: false,
            data: [],
            granularity: 'hourly'
        },
        // Email preview drawer
        emailPreview: {
            loading: false,
            visible: false,
            email: null
        }
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions

        addPaginatedNewslettersToState(data) {
            this.newslettersPaginated.items = data.items;
            this.newslettersPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.newslettersPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.newslettersPaginated.link = _.cloneDeep(data.config.link);
            this.newslettersPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.newslettersPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.newslettersPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.newslettersPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.newslettersPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.newslettersPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.newslettersPaginated.filters, key);
            } else {
                Vue.set(this.newslettersPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            this.newslettersPaginated.path = value;
        },

        defineSorting({ key, value }) {
            this.newslettersPaginated.variables.order = key;
            this.newslettersPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.newslettersPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui.form.initialized = false;
            this.ui.form.validateOnEnter = false;
        },

        insertNewsletter(newsletter, preserveClientState = true) {
            delete newsletter.page;
            // Preserve client-side only state that is not part of the API response
            // unless explicitly resetting (preserveClientState = false)
            if (preserveClientState) {
                const clientSideState = {
                    calculatedRecipientCount: this.form.calculatedRecipientCount,
                    calculatedRecipientCountLoading: this.form.calculatedRecipientCountLoading,
                };
                this.form = {
                    ...newsletter,
                    ...clientSideState,
                };
            } else {
                this.form = newsletter;
            }
        },

        updateNewsletterPartial(newsletter) {
            // Merge newsletter data without replacing relations that might be missing
            Object.keys(newsletter).forEach(key => {
                if (key !== 'page' && key !== 'subscriptionTopic') {
                    Vue.set(this.form, key, newsletter[key]);
                } else if (key === 'subscriptionTopic' && newsletter[key]) {
                    // Only update subscriptionTopic if it exists in the payload
                    Vue.set(this.form, key, newsletter[key]);
                }
            });
        },

        updateSubscriptionTopicOptionsLoading(value) {
            this.options.subscriptionTopic.loading = value;
        },

        insertSubscriptionTopicOptions(subscriptionTopics) {
            this.options.subscriptionTopic.items = subscriptionTopics;
        },

        updateCustomerGroupOptionsLoading(value) {
            this.options.customerGroup.loading = value;
        },

        insertCustomerGroupOptions(customerGroups) {
            this.options.customerGroup.items = customerGroups;
        },

        updateMemberTagOptionsLoading(value) {
            this.options.memberTag.loading = value;
        },

        insertMemberTagOptions(memberTags) {
            this.options.memberTag.items = memberTags;
        },

        updateCampaignOptionsLoading(value) {
            this.options.campaign.loading = value;
        },

        insertCampaignOptions(campaigns) {
            this.options.campaign.items = campaigns;
        },

        // Form field setters
        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        setFormSubject(value) {
            this.form.subject = value;
        },

        setFormPreheader(value) {
            this.form.preheader = value;
        },

        setFormSubscriptionTopics(value) {
            this.form.subscriptionTopics = value;
        },

        setFormCustomerGroups(value) {
            this.form.customerGroups = value;
        },

        setFormMemberTags(value) {
            this.form.memberTags = value;
        },

        setFormCampaign(value) {
            this.form.campaign = value;
        },

        // Statistics setters for real-time updates
        setFormSentEmailsCount(value) {
            Vue.set(this.form, 'sentEmailsCount', value);
        },

        setFormOpens(value) {
            Vue.set(this.form, 'opens', value);
        },

        setFormClicks(value) {
            Vue.set(this.form, 'clicks', value);
        },

        setFormBounces(value) {
            Vue.set(this.form, 'bounces', value);
        },

        setFormReceived(value) {
            Vue.set(this.form, 'received', value);
        },

        setFormSkipped(value) {
            Vue.set(this.form, 'skipped', value);
        },

        // Recipient count setters
        setFormCalculatedRecipientCount(value) {
            this.form.calculatedRecipientCount = value;
        },

        setFormCalculatedRecipientCountLoading(value) {
            this.form.calculatedRecipientCountLoading = value;
        },

        // Sent emails paginated mutations
        setSentEmailsPaginatedLoading(value) {
            this.sentEmailsPaginated.loading = value;
        },

        addPaginatedSentEmailsToState(data) {
            this.sentEmailsPaginated.items = data.items;
            this.sentEmailsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.sentEmailsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.sentEmailsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.sentEmailsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineSentEmailsActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.sentEmailsPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });
            if (activeColumns.length) {
                this.sentEmailsPaginated.activeColumns = activeColumns;
            }
        },

        defineSentEmailsItemsPerPage(perPage) {
            this.sentEmailsPaginated.pageInfo.perPage = perPage;
        },

        defineSentEmailsCurrentPage(currentPage) {
            this.sentEmailsPaginated.pageInfo.currentPage = currentPage;
        },

        defineSentEmailsFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.sentEmailsPaginated.filters, key);
            } else {
                Vue.set(this.sentEmailsPaginated.filters, key, value);
            }
        },

        defineSentEmailsFilterVisibility(value) {
            this.sentEmailsPaginated.showFilters = value;
        },

        defineSentEmailsOrder({ order, direction }) {
            this.sentEmailsPaginated.variables.order = order;
            this.sentEmailsPaginated.variables.direction = direction;
        },

        resetSentEmailsPaginated() {
            this.sentEmailsPaginated.items = [];
            this.sentEmailsPaginated.pageInfo = { total: 0, perPage: 25, currentPage: 1, lastPage: 0 };
            this.sentEmailsPaginated.filters = {};
        },

        // URL clicks mutations
        setUrlClicksLoading(value) {
            this.urlClicks.loading = value;
        },

        setUrlClicksVisible(value) {
            this.urlClicks.visible = value;
        },

        setUrlClicksItems(items) {
            this.urlClicks.items = items;
        },

        // URL clickers mutations
        setUrlClickersLoading(value) {
            this.urlClickers.loading = value;
        },

        setUrlClickersItems(items) {
            this.urlClickers.items = items;
        },

        setUrlClickersUrl(url) {
            this.urlClickers.url = url;
        },

        setUrlClickersPageInfo(pageInfo) {
            this.urlClickers.pageInfo = _.cloneDeep(pageInfo);
        },

        resetUrlClickers() {
            this.urlClickers.loading = false;
            this.urlClickers.items = [];
            this.urlClickers.url = null;
            this.urlClickers.pageInfo = {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            };
        },

        // Bulk tagging mutations
        setBulkTaggingLoading(value) {
            this.bulkTagging.loading = value;
        },

        // Statistics timeline mutations
        setStatisticsTimelineLoading(value) {
            this.statisticsTimeline.loading = value;
        },

        setStatisticsTimelineData(data) {
            this.statisticsTimeline.data = data;
        },

        setStatisticsTimelineGranularity(granularity) {
            this.statisticsTimeline.granularity = granularity;
        },

        // Email preview mutations
        setEmailPreviewLoading(value) {
            this.emailPreview.loading = value;
        },

        setEmailPreviewVisible(value) {
            this.emailPreview.visible = value;
        },

        setEmailPreviewEmail(email) {
            this.emailPreview.email = email;
        },

        // Async actions

        fetchNewslettersPaginated() {
            return NewsletterApi.fetchNewslettersPaginated(this.newslettersPaginated).then(data => {
                this.addPaginatedNewslettersToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchNewsletter(payload) {
            const setupStore = useSetupStore();
            // Support both old format (just id) and new format (object with options)
            const id = typeof payload === 'object' ? payload.id : payload;
            const skipPage = typeof payload === 'object' ? (payload.skipPage || false) : false;
            const skipSubscriptionTopics = typeof payload === 'object' ? (payload.skipSubscriptionTopics || false) : false;

            const cms = setupStore.activeCms;
            if (cms) {
                return NewsletterApi.fetchNewsletter(id, cms.id, skipPage, skipSubscriptionTopics).then(newsletter => {
                    // Use partial update if we're skipping fields to preserve existing data
                    if (skipPage || skipSubscriptionTopics) {
                        this.updateNewsletterPartial(_.cloneDeep(newsletter));
                    } else {
                        this.insertNewsletter(_.cloneDeep(newsletter));
                    }
                    this.resetRules();
                    return newsletter;
                });
            }
        },

        fetchSubscriptionTopicOptions(cmsID) {
            this.updateSubscriptionTopicOptionsLoading(true);
            return SubscriptionTopicApi.fetchList(cmsID)
                .then(subscriptionTopics => {
                    this.updateSubscriptionTopicOptionsLoading(false);
                    this.insertSubscriptionTopicOptions(subscriptionTopics);
                })
                .catch(err => {
                    this.updateSubscriptionTopicOptionsLoading(false);
                    throw err;
                });
        },

        fetchCampaignOptions(cmsID) {
            this.updateCampaignOptionsLoading(true);
            return CampaignApi.fetchList(cmsID)
                .then(campaigns => {
                    this.updateCampaignOptionsLoading(false);
                    this.insertCampaignOptions(campaigns);
                })
                .catch(err => {
                    this.updateCampaignOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(shopId) {
            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopId)
                .then(customerGroups => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchMemberTagOptions(cmsId) {
            this.updateMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsId)
                .then(memberTags => {
                    this.updateMemberTagOptionsLoading(false);
                    this.insertMemberTagOptions(memberTags);
                })
                .catch(err => {
                    this.updateMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchMemberTagsBySearch(query) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (!cms) return Promise.resolve([]);
            return MemberTagApi.search(cms.id, query);
        },

        initializeNewsletter(key) {
            this.resetRules();
        },

        send(payload) {
            return NewsletterApi.send(payload).then(newsletter => {
                if (newsletter) {
                    // Only update scheduling-related fields to avoid triggering
                    // heavy watchers (subscriptionTopics, customerGroups, memberTags)
                    Vue.set(this.form, 'start_sending_at', newsletter.start_sending_at);
                    Vue.set(this.form, 'started_sending_at', newsletter.started_sending_at);
                    Vue.set(this.form, 'finished_sending_at', newsletter.finished_sending_at);
                    Vue.set(this.form, 'status', newsletter.status);
                }
                return newsletter;
            });
        },

        cancelPlannedNewsletter(id) {
            return NewsletterApi.cancelPlanned(id).then(newsletter => {
                this.insertNewsletter(_.cloneDeep(newsletter));
                this.resetRules();
                return newsletter;
            });
        },

        reschedulePlannedNewsletter(id, scheduledAt) {
            return NewsletterApi.reschedulePlanned(id, scheduledAt).then(newsletter => {
                this.insertNewsletter(_.cloneDeep(newsletter));
                this.resetRules();
                return newsletter;
            });
        },

        create(newsletter) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (cms) {
                newsletter.cms = { id: cms.id };
                return NewsletterApi.create(newsletter).then(newsletter => {
                    this.insertNewsletter(_.cloneDeep(newsletter));
                    this.resetRules();
                });
            }
        },

        update(newsletter) {
            return NewsletterApi.update(newsletter).then(newsletter => {
                this.insertNewsletter(_.cloneDeep(newsletter));
                this.resetRules();
            });
        },

        deleteNewsletter(id) {
            return NewsletterApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertNewsletter(_.cloneDeep(form), false);
        },

        fetchRecipientCount() {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (!cms) return Promise.resolve(null);

            const subscriptionTopics = _.get(this.form, 'subscriptionTopics') || [];
            const customerGroups = _.get(this.form, 'customerGroups') || [];
            const memberTags = _.get(this.form, 'memberTags') || [];

            // If no subscription topics selected, set count to 0
            if (subscriptionTopics.length === 0) {
                this.setFormCalculatedRecipientCount(0);
                return Promise.resolve(0);
            }

            this.setFormCalculatedRecipientCountLoading(true);

            const variables = {
                cms_id: parseInt(cms.id, 10),
                subscription_topic_ids: subscriptionTopics.map(t => parseInt(t.id, 10)),
                customer_group_ids: customerGroups.map(g => parseInt(g.id, 10)),
                member_tag_ids: memberTags.map(t => parseInt(t.id, 10)),
            };

            return apolloClient.query({
                query: RecipientCountQuery,
                variables,
                fetchPolicy: 'network-only',
            })
                .then(response => {
                    const count = _.get(response, 'data.EX_NWSLTR_RecipientCount', 0);
                    this.setFormCalculatedRecipientCount(count);
                    this.setFormCalculatedRecipientCountLoading(false);
                    return count;
                })
                .catch(error => {
                    console.error('Error fetching recipient count:', error);
                    this.setFormCalculatedRecipientCount(null);
                    this.setFormCalculatedRecipientCountLoading(false);
                    throw error;
                });
        },

        // Sent emails paginated actions
        fetchSentEmailsPaginated(payload) {
            const newsletterId = typeof payload === 'object' ? payload.newsletterId : payload;
            const silent = typeof payload === 'object' ? (payload.silent || false) : false;

            if (!silent) {
                this.setSentEmailsPaginatedLoading(true);
            }
            return SentEmailApi.fetchPaginated(newsletterId, this.sentEmailsPaginated)
                .then(data => {
                    this.addPaginatedSentEmailsToState(data);
                    const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                    this.defineSentEmailsActiveColumns(activeColumnKeys);
                    this.setSentEmailsPaginatedLoading(false);
                    return data;
                })
                .catch(err => {
                    this.setSentEmailsPaginatedLoading(false);
                    throw err;
                });
        },

        // Email preview actions
        fetchEmailPreview(emailId) {
            this.setEmailPreviewLoading(true);
            return SentEmailApi.fetchSingle(emailId)
                .then(email => {
                    this.setEmailPreviewEmail(email);
                    this.setEmailPreviewLoading(false);
                    this.setEmailPreviewVisible(true);
                    return email;
                })
                .catch(err => {
                    this.setEmailPreviewLoading(false);
                    throw err;
                });
        },

        closeEmailPreview() {
            this.setEmailPreviewVisible(false);
            this.setEmailPreviewEmail(null);
        },

        // URL clicks actions
        fetchUrlClicks(newsletterId) {
            this.setUrlClicksLoading(true);
            return SentEmailApi.fetchUrlClicks(newsletterId)
                .then(items => {
                    this.setUrlClicksItems(items);
                    this.setUrlClicksLoading(false);
                    this.setUrlClicksVisible(true);
                    return items;
                })
                .catch(err => {
                    this.setUrlClicksLoading(false);
                    throw err;
                });
        },

        closeUrlClicks() {
            this.setUrlClicksVisible(false);
            this.resetUrlClickers();
        },

        // URL clickers actions
        fetchUrlClickers({ newsletterId, url, currentPage, perPage }) {
            this.setUrlClickersLoading(true);
            this.setUrlClickersUrl(url);
            return SentEmailApi.fetchUrlClickers(newsletterId, url, currentPage, perPage)
                .then(function(response) {
                    this.setUrlClickersItems(response.items);
                    this.setUrlClickersPageInfo(response.pageInfo);
                    this.setUrlClickersLoading(false);
                    return response;
                }.bind(this))
                .catch(function(err) {
                    this.setUrlClickersLoading(false);
                    throw err;
                }.bind(this));
        },

        // Bulk tagging actions
        bulkAddTagsToClickers(cmsId, emails, tags) {
            this.setBulkTaggingLoading(true);
            return RestrictedAreaMemberTagApi.bulkAddTags(cmsId, emails, tags)
                .then(function(result) {
                    this.setBulkTaggingLoading(false);
                    return result;
                }.bind(this))
                .catch(function(err) {
                    this.setBulkTaggingLoading(false);
                    throw err;
                }.bind(this));
        },

        // Statistics timeline actions
        fetchStatisticsTimeline({ newsletterId, granularity, start, end, maxTimespan, silent }) {
            if (!silent) {
                this.setStatisticsTimelineLoading(true);
            }
            return SentEmailApi.fetchStatisticsTimeline(newsletterId, granularity, start, end, maxTimespan)
                .then(data => {
                    this.setStatisticsTimelineData(data);
                    // Get granularity from first data point (all points have same granularity)
                    if (data && data.length > 0 && data[0].granularity) {
                        this.setStatisticsTimelineGranularity(data[0].granularity);
                    }
                    this.setStatisticsTimelineLoading(false);
                    return data;
                })
                .catch(err => {
                    this.setStatisticsTimelineLoading(false);
                    throw err;
                });
        }
    }
});
